import type { ConflictResult, ConflictRule } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import { isoWeekdayFromDateKey } from '@zag/calendar/models';
import { isEmployedOn, isWorkingOn, type EmploymentRow } from '@zag/timetracking/common';

/** Flags persons scheduled outside their working days; employment rows decide, team weekdays only without rows. */
export class ClosedDayConflictRule implements ConflictRule {
  readonly type = 'closed_day' as const;

  constructor(
    private readonly workingDays: number[],
    private readonly personRows: ReadonlyMap<string, EmploymentRow[]> = new Map()
  ) {}

  detect(schedule: ScheduleMap): ConflictResult[] {
    const results: ConflictResult[] = [];
    const workingDaySet = new Set(this.workingDays);

    for (const [dateStr, daySchedule] of Object.entries(schedule)) {
      // Derive ISO weekday (0=Mon…6=Sun) directly from the date string — no Date needed
      const isoWeekday = isoWeekdayFromDateKey(dateStr) ?? -1;
      const isTeamWorkingDay = workingDaySet.has(isoWeekday);

      for (const personId of Object.keys(daySchedule)) {
        const rows = this.personRows.get(personId);
        // Dates outside employment are EmploymentInactiveConflictRule's, not closed days.
        if (rows && !isEmployedOn(rows, dateStr)) continue;
        const isWorkingDay = rows ? isWorkingOn(rows, dateStr) : isTeamWorkingDay;
        if (isWorkingDay) continue;

        results.push({
          type: 'closed_day',
          date: dateStr,
          personId,
          eventLabel: 'Closed day',
          calendarId: 'strategy',
        });
      }
    }

    return results;
  }
}
